import { useState } from "react";
import { db } from "../services/firebase";
import { useAuth } from "../context/AuthContext";
import { doc, setDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { Activity, ChevronRight, ChevronLeft, Plus, Trash2 } from "lucide-react";
import { validateWeight } from "../utils/weightValidation";
import { isSavableHistoryRow, normalizeWeightForWrite } from "../utils/entryContent";
import { localDateString } from "../utils/entryDate";
import { calcTherapyWeek } from "../utils/therapyStats";
import { validateEntryDate, MIN_ENTRY_DATE } from "../utils/dateValidation";
import { trackEvent } from "../services/analytics";
import { THERAPY_REASON_OPTIONS } from "../data/therapyReasonOptions";

type UserType = "new" | "existing" | null;
type TherapyReason = "cukorbetegseg" | "testsulycsokkenes" | "mindketto" | null;

interface HistoryEntry {
  date: string;
  weight: string;
  dose: string;
}

interface OnboardingProps {
  onComplete: () => void;
}

export default function Onboarding({ onComplete }: OnboardingProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState<UserType>(null);
  const [saving, setSaving] = useState(false);

  // Profil adatok
  // 2026-08-17: nincs előre kiválasztott gyógyszer — ha a user kihagyja ezt
  // a lépést, a mező üresen marad Firestore-ban is, hogy a klinikai
  // összehasonlítás (selectClinicalBranch) helyesen üres állapotot mutasson
  // ahelyett, hogy csendben rossz vizsgálati görbét (pl. SURMOUNT-1 egy
  // valójában semaglutidot szedő usernek) jelenítene meg.
  const [gyogyszer, setGyogyszer] = useState("");
  const [terapiaKezdete, setTerapiaKezdete] = useState("");
  const [elsoInjekció, setElsoInjekció] = useState("");
  const [kezdoSuly, setKezdoSuly] = useState("");
  const [celSuly, setCelSuly] = useState("");
  const kezdoSulyError = kezdoSuly === "" ? null : validateWeight(kezdoSuly);
  const [therapyReason, setTherapyReason] = useState<TherapyReason>(null);
  const [glucoseOptIn, setGlucoseOptIn] = useState(true);
  const offerGlucose = therapyReason === "cukorbetegseg" || therapyReason === "mindketto";

  // Visszatérő user — korábbi bejegyzések
  const [history, setHistory] = useState<HistoryEntry[]>([
    { date: "", weight: "", dose: "" }
  ]);

  const addHistoryRow = () => {
    setHistory(prev => [...prev, { date: "", weight: "", dose: "" }]);
  };

  const removeHistoryRow = (i: number) => {
    setHistory(prev => prev.filter((_, idx) => idx !== i));
  };

  const updateHistory = (i: number, field: keyof HistoryEntry, value: string) => {
    setHistory(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  };

  const handleComplete = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const glucoseEnabled = offerGlucose && glucoseOptIn;

      // Profil mentése — merge: true, hogy ha ez egy már meglévő (pl. a
      // Profil-mentés mergehiánya miatt sérült) profilt ír felül, ne
      // törölje ki azokat a mezőket, amiket ez a payload nem tartalmaz
      // (pl. aiEnabled).
      await setDoc(doc(db, "profiles", user.uid), {
        gyogyszer,
        terapiaKezdete,
        kezdoSuly,
        celSuly,
        onboardingDone: true,
        therapyReason,
        glucoseEnabled,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      if (glucoseEnabled) trackEvent("glucose_logging_enabled");

      // Első injekció bejegyzés mentése (ha megadta)
      if (elsoInjekció && kezdoSuly && !validateEntryDate(elsoInjekció)) {
        await addDoc(collection(db, "entries"), {
          userId: user.uid,
          weight: normalizeWeightForWrite(parseFloat(kezdoSuly)),
          dose: 0,
          mood: 0,
          appetite: 0,
          mellekhat: {},
          date: elsoInjekció,
          createdAt: serverTimestamp(),
        });
      }

      // Visszatérő user — korábbi bejegyzések mentése
      if (userType === "existing") {
        // A canNext() a "history" lépésen már blokkol érvénytelen súlynál,
        // de ez a szűrés önmagában is védjen — ne csak a UI-n múljon.
        // 2026-09-15: a súly már NEM feltétel (D2) — dátum + (súly VAGY
        // dózis) kell. A döntés kanonikus helye: utils/entryContent.ts.
        const validRows = history.filter(isSavableHistoryRow);
        for (const row of validRows) {
          await addDoc(collection(db, "entries"), {
            userId: user.uid,
            weight: normalizeWeightForWrite(row.weight ? parseFloat(row.weight) : ""),
            dose: row.dose ? parseFloat(row.dose) : 0,
            mood: 0,
            appetite: 0,
            mellekhat: {},
            date: row.date,
            createdAt: serverTimestamp(),
          });
        }
      }

      onComplete();
    } catch (err) {
      console.error("Onboarding mentési hiba:", err);
    } finally {
      setSaving(false);
    }
  };

  // ── Lépések meghatározása ──
  const newUserSteps = ["type", "reason", "drug", "therapy", "weight", "done"];
  const existingUserSteps = ["type", "reason", "drug", "therapy", "weight", "history", "done"];
  const steps = userType === "existing" ? existingUserSteps : newUserSteps;
  const currentStepName = steps[step];
  const progress = Math.round((step / (steps.length - 1)) * 100);

  // 2026-08-17: a therapyReason az egyetlen mező, ami tényleg nem vezethető
  // le semmiből — a gyógyszer, a terápia kezdete és a kezdő súly mind
  // kiolvasható lenne az első naplózott bejegyzésből (resolveBaseWeight és
  // a therapyWeek-fallback már ma is erre esik vissza, ha a profil-mező
  // üres). Ezért ezek a lépések itt kihagyhatók — csak akkor blokkolunk,
  // ha a user tényleg beírt valamit, de az érvénytelen.
  const canNext = () => {
    if (currentStepName === "type") return userType !== null;
    if (currentStepName === "reason") return true;
    if (currentStepName === "drug") return true;
    if (currentStepName === "therapy") return true;
    if (currentStepName === "weight") return kezdoSuly === "" || !kezdoSulyError;
    // 2026-08-18: a visszamenőleges bejegyzés-sorok korábban nem validáltak —
    // egy elgépelt súly (pl. tizedesvessző nélkül) érvényesítés nélkül jutott
    // be az entries collectionbe. Lásd docs/FOGALMAK.md.
    if (currentStepName === "history") return history.every((r) => !r.weight || !validateWeight(r.weight));
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex flex-col">
      {/* Header */}
      <div className="px-5 pt-12 pb-6">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
            <Activity size={16} className="text-white" />
          </div>
          <span className="font-bold text-gray-900">GLP Napló</span>
        </div>

        {/* Progress bar */}
        {step > 0 && currentStepName !== "done" && (
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2">
            <div
              className="bg-emerald-600 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 px-5 pb-8">

        {/* ── 0. Típus választás ── */}
        {currentStepName === "type" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Üdv a GLP Naplóban! 👋</h1>
              <p className="text-gray-500 text-sm">Segítünk beállítani mindent. Először mondd el, hol tartasz:</p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => setUserType("new")}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                  userType === "new"
                    ? "border-emerald-600 bg-emerald-50"
                    : "border-gray-200 bg-white hover:border-emerald-300"
                }`}
              >
                <p className="font-semibold text-gray-900 mb-1">🌱 Most kezdem a terápiát</p>
                <p className="text-sm text-gray-500">Az első vagy néhány első injekciómnál tartok</p>
              </button>

              <button
                onClick={() => setUserType("existing")}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                  userType === "existing"
                    ? "border-emerald-600 bg-emerald-50"
                    : "border-gray-200 bg-white hover:border-emerald-300"
                }`}
              >
                <p className="font-semibold text-gray-900 mb-1">📊 Már egy ideje csinálom</p>
                <p className="text-sm text-gray-500">Heteket vagy hónapokat töltöttem a terápiával — szeretném visszamenőleg is rögzíteni</p>
              </button>
            </div>
          </div>
        )}

        {/* ── Miért kaptad a terápiát? ── */}
        {currentStepName === "reason" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Miért kaptad a terápiát?</h1>
              <p className="text-gray-500 text-sm">Ez segít testreszabni az appot — bármikor kihagyható.</p>
            </div>
            <div className="space-y-3">
              {THERAPY_REASON_OPTIONS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setTherapyReason(r.id)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                    therapyReason === r.id
                      ? "border-emerald-600 bg-emerald-50"
                      : "border-gray-200 bg-white hover:border-emerald-300"
                  }`}
                >
                  <p className="font-semibold text-gray-900">{r.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── 1. Gyógyszer ── */}
        {currentStepName === "drug" && (
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">2. lépés</p>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Melyik gyógyszert szeded?</h1>
              <p className="text-gray-500 text-sm">Ez meghatározza a dózis sémát és a klinikai összehasonlítást. Kihagyható — utólag a Profilban választhatod ki.</p>
            </div>
            <div className="space-y-3">
              {[
                { id: "tirzepatide", name: "Tirzepatide", brands: "Mounjaro, Zepbound", desc: "GLP-1 + GIP kettős agonista", doses: "2.5 → 15 mg" },
                { id: "semaglutide", name: "Semaglutide", brands: "Ozempic, Wegovy", desc: "GLP-1 receptor agonista", doses: "0.25 → 2.4 mg" },
              ].map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGyogyszer(g.id)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                    gyogyszer === g.id
                      ? "border-emerald-600 bg-emerald-50"
                      : "border-gray-200 bg-white hover:border-emerald-300"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{g.name}</p>
                      <p className="text-xs text-emerald-600 font-medium mb-1">{g.brands}</p>
                      <p className="text-sm text-gray-500">{g.desc}</p>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg ml-2 flex-shrink-0">{g.doses}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── 2. Terápia kezdete ── */}
        {currentStepName === "therapy" && (
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">3. lépés</p>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Mikor kezdted a terápiát?</h1>
              <p className="text-gray-500 text-sm">Az első injekció dátuma — ez alapján számítjuk a terápia hetét. Kihagyható: ekkor az első naplózott bejegyzésed dátumát használjuk.</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-gray-200 space-y-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">Terápia kezdete (első injekció)</label>
                <input
                  type="date"
                  value={terapiaKezdete}
                  onChange={(e) => { setTerapiaKezdete(e.target.value); setElsoInjekció(e.target.value); }}
                  min={MIN_ENTRY_DATE}
                  max={localDateString(new Date())}
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
              </div>
              {/* A kanonikus calcTherapyWeek (A7, 2026-10-14) — a Főoldallal azonos
                  hét; 0. hétnél (a kezdés napjának hajnala) nem írunk „0. hét"-et. */}
              {terapiaKezdete && calcTherapyWeek(new Date(terapiaKezdete)) > 0 && (
                <div className="bg-emerald-50 rounded-xl p-3">
                  <p className="text-xs text-emerald-700 font-medium">
                    {calcTherapyWeek(new Date(terapiaKezdete))}. terápiás héten jársz 🎉
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── 3. Súly ── */}
        {currentStepName === "weight" && (
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">4. lépés</p>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Súly adatok</h1>
              <p className="text-gray-500 text-sm">Ezek alapján számítjuk a fogyást és a klinikai célokat. Kihagyható: ekkor az első naplózott súlyodat használjuk.</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-gray-200 space-y-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">Kezdő súly (kg)</label>
                <input
                  type="number"
                  value={kezdoSuly}
                  onChange={(e) => setKezdoSuly(e.target.value)}
                  placeholder="pl. 115"
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                {kezdoSulyError && <p className="mt-1 text-xs text-red-600">{kezdoSulyError}</p>}
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1.5">Célsúly (kg) — opcionális</label>
                <input
                  type="number"
                  value={celSuly}
                  onChange={(e) => setCelSuly(e.target.value)}
                  placeholder="pl. 85"
                  className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
              </div>
              {kezdoSuly && celSuly && parseFloat(kezdoSuly) > parseFloat(celSuly) && (
                <div className="bg-emerald-50 rounded-xl p-3">
                  <p className="text-xs text-emerald-700 font-medium">
                    Cél: -{(parseFloat(kezdoSuly) - parseFloat(celSuly)).toFixed(1)} kg fogyás
                    ({((parseFloat(kezdoSuly) - parseFloat(celSuly)) / parseFloat(kezdoSuly) * 100).toFixed(0)}%)
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── 4. Korábbi bejegyzések (csak existing user) ── */}
        {currentStepName === "history" && (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">5. lépés</p>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Korábbi adatok</h1>
              <p className="text-gray-500 text-sm">Add meg a korábbi méréseidet — legalább a fontosabb mérföldköveket. Kihagyható, de a statisztikák pontosabbak lesznek.</p>
            </div>

            <div className="space-y-3">
              {history.map((row, i) => (
                <div key={i} className="bg-white rounded-2xl p-3 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500">{i + 1}. bejegyzés</span>
                    {history.length > 1 && (
                      <button onClick={() => removeHistoryRow(i)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-1">
                      <label className="text-xs text-gray-400 block mb-1">Dátum</label>
                      <input
                        type="date"
                        value={row.date}
                        onChange={(e) => updateHistory(i, "date", e.target.value)}
                        min={MIN_ENTRY_DATE}
                        max={localDateString(new Date())}
                        className="w-full border border-gray-200 rounded-lg p-2 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Súly (kg)</label>
                      <input
                        type="number"
                        value={row.weight}
                        onChange={(e) => updateHistory(i, "weight", e.target.value)}
                        placeholder="95"
                        className="w-full border border-gray-200 rounded-lg p-2 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      />
                      {row.weight && validateWeight(row.weight) && (
                        <p className="mt-1 text-[10px] text-red-600">{validateWeight(row.weight)}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Dózis (mg)</label>
                      <select
                        value={row.dose}
                        onChange={(e) => updateHistory(i, "dose", e.target.value)}
                        className="w-full border border-gray-200 rounded-lg p-2 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      >
                        <option value="">–</option>
                        {gyogyszer === "tirzepatide"
                          ? [2.5, 5, 7.5, 10, 12.5, 15].map(d => <option key={d} value={d}>{d}</option>)
                          : [0.25, 0.5, 1, 1.7, 2.4].map(d => <option key={d} value={d}>{d}</option>)
                        }
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addHistoryRow}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-emerald-300 text-emerald-600 py-3 rounded-2xl text-sm font-medium hover:bg-emerald-50 transition-colors"
            >
              <Plus size={16} /> Sor hozzáadása
            </button>

            <p className="text-xs text-gray-400 text-center">
              Dátum kell, és legalább súly vagy dózis. Üres sorokat kihagyjuk.
            </p>
          </div>
        )}

        {/* ── Kész ── */}
        {currentStepName === "done" && (
          <div className="space-y-6 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-4xl">
              🎉
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Minden kész!</h1>
              <p className="text-gray-500 text-sm">
                {userType === "existing"
                  ? "Profil és korábbi adatok mentve. A statisztikáid azonnal elérhetők."
                  : "Profil beállítva. Kezdj el naplózni az első injekciódat!"}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-gray-100 text-left space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Összefoglalás</p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Gyógyszer</span>
                <span className="font-semibold text-gray-800">
                  {gyogyszer === "tirzepatide" ? "Tirzepatide" : gyogyszer === "semaglutide" ? "Semaglutide" : "–"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Terápia kezdete</span>
                <span className="font-semibold text-gray-800">
                  {terapiaKezdete ? new Date(terapiaKezdete).toLocaleDateString("hu-HU", { month: "long", day: "numeric" }) : "–"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Kezdő súly</span>
                <span className="font-semibold text-gray-800">{kezdoSuly ? `${kezdoSuly} kg` : "–"}</span>
              </div>
              {celSuly && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Célsúly</span>
                  <span className="font-semibold text-gray-800">{celSuly} kg</span>
                </div>
              )}
              {userType === "existing" && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Korábbi bejegyzések</span>
                  <span className="font-semibold text-gray-800">{history.filter(isSavableHistoryRow).length} db</span>
                </div>
              )}
            </div>

            {offerGlucose && (
              <label className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-gray-100 text-left cursor-pointer">
                <input
                  type="checkbox"
                  checked={glucoseOptIn}
                  onChange={(e) => setGlucoseOptIn(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-emerald-600 flex-shrink-0"
                />
                <span className="text-sm text-gray-700">
                  Éhomi vércukor naplózásának bekapcsolása — bármikor módosíthatod a Profil oldalon.
                </span>
              </label>
            )}
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="px-5 pb-10 space-y-3">
        {currentStepName === "done" ? (
          <button
            onClick={handleComplete}
            disabled={saving}
            className="w-full bg-emerald-700 text-white py-4 rounded-2xl font-semibold text-base hover:bg-emerald-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? (
              <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              "Belépés az appba →"
            )}
          </button>
        ) : (
          <button
            onClick={() => setStep(s => s + 1)}
            disabled={!canNext()}
            className="w-full bg-emerald-700 text-white py-4 rounded-2xl font-semibold text-base hover:bg-emerald-800 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            Tovább <ChevronRight size={18} />
          </button>
        )}

        {step > 0 && currentStepName !== "done" && (
          <button
            onClick={() => setStep(s => s - 1)}
            className="w-full flex items-center justify-center gap-1 text-gray-400 text-sm py-2 hover:text-gray-600"
          >
            <ChevronLeft size={16} /> Vissza
          </button>
        )}

        {currentStepName !== "done" && (
          <button
            onClick={handleComplete}
            disabled={saving}
            className="w-full text-center text-xs text-gray-300 hover:text-gray-500 py-1 disabled:opacity-60"
          >
            Kihagyom, később beállítom
          </button>
        )}
      </div>
    </div>
  );
}
